// Puts the race half together for the app loop: frames from the active source go into a race timeline,
// and the playout clock turns wall time into render time 2 s behind the newest frame.
// When the source changes (live ↔ replay) the timeline starts afresh, because arrival times restart
// on the new source's clock; a 'signal' event with status 'source-changed' marks the switch.
// Events skipped by a playout jump are not replayed, except the latest phase and results.
import { PlayoutClock } from './playout';
import { Timeline } from './timeline';
import type { RaceEvent, RaceSnapshot, SignalFrame } from './types';

export type SourceKind = 'live' | 'replay';
export type Connection = 'connecting' | 'open' | 'reconnecting' | 'failed';

export class Broadcast {
  private timeline: Timeline;
  private playout = new PlayoutClock();
  private lastWall = Number.NaN;
  private rate = 1;
  /** Events at or before this render time are not delivered (they belong to skipped time). */
  private floor = -Infinity;
  private skipped: { from: number; to: number } | null = null;
  private pendingSourceEvent = false;
  /** Events not tied to the race clock (signal health, source change), delivered on the next call. */
  private outbox: RaceEvent[] = [];
  kind: SourceKind;
  label: string;
  connection: Connection = 'connecting';

  constructor(kind: SourceKind = 'live', label = '') {
    this.kind = kind;
    this.label = label;
    this.timeline = new Timeline({ source: kind });
  }

  /** The active source changed: start a fresh timeline on the new source's clock. */
  setSource(kind: SourceKind, label: string, rate = 1): void {
    const hadFrames = !Number.isNaN(this.timeline.newestT());
    this.kind = kind;
    this.label = label;
    this.rate = rate;
    this.timeline = new Timeline({ source: kind });
    this.playout = new PlayoutClock();
    this.floor = -Infinity;
    this.skipped = null;
    this.pendingSourceEvent = hadFrames;
  }

  push(frame: SignalFrame, arrivalMs: number): void {
    this.timeline.push(frame, arrivalMs);
  }

  /** Render time for the loop: call once per animation frame with wall-clock ms. */
  clock(wallMs: number): number {
    const dt = Number.isNaN(this.lastWall) ? 0 : Math.max(0, Math.min(1000, wallMs - this.lastWall));
    this.lastWall = wallMs;
    const before = this.playout.renderT;
    const prevHealth = this.playout.health;
    const step = this.playout.advance(dt * this.rate, this.timeline);
    if (Number.isNaN(step.renderT)) return 0;
    if (Number.isNaN(before)) this.floor = step.renderT; // nothing before the first rendered moment fires
    if (step.jumpedFrom !== null) this.skipped = { from: step.jumpedFrom, to: step.renderT };
    this.timeline.setHealth(step.health);
    if (this.pendingSourceEvent) {
      this.pendingSourceEvent = false;
      this.outbox.push({ type: 'signal', t: step.renderT, status: 'source-changed' });
    }
    if (step.health !== prevHealth) this.outbox.push({ type: 'signal', t: step.renderT, status: step.health });
    return step.renderT;
  }

  /** Buffer ahead of render time, ms. */
  get bufferAheadMs(): number {
    return this.playout.ahead;
  }

  sample(renderT: number): RaceSnapshot {
    return this.timeline.sample(renderT);
  }

  eventsBetween(t0: number, t1: number): RaceEvent[] {
    const out = this.outbox;
    this.outbox = [];
    // The first rendered moment counts: events stamped exactly at it still fire.
    let from = Math.max(t0, this.floor - 1e-3);
    const s = this.skipped;
    if (s && from < s.to && t1 >= s.to) {
      // A jump: deliver only the latest phase and results from the skipped stretch.
      const missed = this.timeline.eventsBetween(from, s.to);
      const lastPhase = missed.filter((e) => e.type === 'phase').pop();
      const lastResults = missed.filter((e) => e.type === 'results').pop();
      if (lastPhase) out.push(lastPhase);
      if (lastResults) out.push(lastResults);
      from = s.to;
      this.skipped = null;
    }
    out.push(...this.timeline.eventsBetween(from, t1));
    return out;
  }

  peek(renderT: number, horizonMs: number): RaceEvent[] {
    return this.timeline.peek(renderT, horizonMs);
  }

  newestT(): number {
    return this.timeline.newestT();
  }
}
